'use client';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS, activeConversations, useBrainSessions, useMe } from '../../lib/queries';
import { AUTH_CLEARED_EVENT, subscribeAuthTransitions } from '../../lib/token';
import { usePathname } from 'next/navigation';
import { usePageHeader } from '../../lib/pageHeader';
import { resolveDocumentTitle } from '../../lib/documentTitle';
import { unreadConversationCount } from '../../lib/unreadConversations';
import { useShellNavigation } from './useShellNavigation';

/** The one place the browser tab is named, mounted once above the auth gate.
 *
 *  It keys off the pathname rather than off a route's `metadata` export: every surface here is client
 *  rendered and navigation happens without a document load, so a server-side title would be written once
 *  and never again. The mount above the auth gate also names the login screen with the synchronously seeded
 *  brand, while `allWorlds` keeps hidden and plugin pages named from the same translated labels as the menu.
 *
 *  This write is safe only because the root metadata deliberately renders NO <title> node. The race fixed
 *  in 1d052eba was an effect fighting a React-owned metadata title; with that second owner removed, React has
 *  nothing to re-commit over this value. */
export function DocumentTitle() {
  const [blocked, setBlocked] = useState(false);
  const me = useMe(!blocked);
  const qc = useQueryClient();
  const awaitingProof = useRef(false);
  useEffect(() => {
    // LoginGate owns cache clearing and revalidation. Only its new identity proof may reopen this
    // private observer; seeing a cached identity or an intermediate empty render is not proof.
    const unsubscribeCache = qc.getQueryCache().subscribe(event => {
      if (awaitingProof.current && event.type === 'updated' && event.action.type === 'success'
          && event.query.queryKey.length === 1 && event.query.queryKey[0] === QUERY_KEYS.me[0]) {
        awaitingProof.current = false;
        setBlocked(false);
      }
    });
    const unsubscribe = subscribeAuthTransitions(transition => {
      if (transition.phase === 'start') { awaitingProof.current = false; setBlocked(true); }
      else if (transition.phase === 'ready') { awaitingProof.current = false; setBlocked(false); }
      else awaitingProof.current = true;
    });
    const clear = () => { awaitingProof.current = true; setBlocked(true); };
    window.addEventListener(AUTH_CLEARED_EVENT, clear);
    return () => { unsubscribeCache(); unsubscribe(); window.removeEventListener(AUTH_CLEARED_EVENT, clear); };
  }, [qc]);
  return me.data?.user && !blocked ? <AuthenticatedTitle /> : <TitleWriter />;
}

function AuthenticatedTitle() {
  const sessions = useBrainSessions();
  const unreadCount = unreadConversationCount(activeConversations(sessions.data ?? []));
  return <TitleWriter unreadCount={unreadCount} />;
}

function TitleWriter({ unreadCount }: { unreadCount?: number }) {
  const pathname = usePathname();
  const { allWorlds } = useShellNavigation();
  const headerTitle = usePageHeader()?.header.title;

  useEffect(() => {
    document.title = resolveDocumentTitle({ pathname: pathname ?? '', entries: allWorlds, headerTitle, unreadCount });
  }, [pathname, allWorlds, headerTitle, unreadCount]);

  return null;
}
